import type { UpdateUserDto, UserDto, UserWithSecrets } from "@reactive-resume/dto";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";

import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";

export const updateUser = async (data: UpdateUserDto) => {
  const response = await axios.patch<UserDto, AxiosResponse<UserDto>, UpdateUserDto>(
    "/user/me",
    data,
  );

  return response.data;
};

export const useUpdateUser = () => {
  const {
    error,
    isPending: loading,
    mutateAsync: updateUserFn,
  } = useMutation({
    mutationFn: updateUser,
    onSuccess: (data) => {
      queryClient.setQueryData(["user"], data);
    },
  });

  return { updateUser: updateUserFn, loading, error };
};

export interface UpdateUserAdminDto {
  name?: string;
  email?: string;
  isActivated?: boolean;
}

export const updateUserByAdmin = async (
  userId: string,
  data: UpdateUserAdminDto,
): Promise<UserWithSecrets> => {
  const response = await axios.patch<UserWithSecrets>(`/user/admin/${userId}`, data);
  return response.data;
};

export const useUpdateUserByAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateUserAdminDto }) =>
      updateUserByAdmin(userId, data),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ["filter-admin"] });
      queryClient.invalidateQueries({ queryKey: ["filter-user"] });
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
    },
  });
};
